import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { COUNTRY_DIVISIONS, getCountry, getCountryCode } from '../services/country';
import MunicipalitySelect from '../components/account/MunicipalitySelect';
import styles from './Auth.module.css';

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email:'', password:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [linkedinEnabled, setLinkedinEnabled] = useState(false);

  useEffect(() => { api.get('/auth/linkedin/config').then(({data})=>setLinkedinEnabled(data.enabled)).catch(()=>{}); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const d = await login(form.email, form.password);
      navigate(d.role === 'admin' ? '/dashboard/admin' : d.role === 'company' ? '/dashboard/company' : '/dashboard/candidate');
    } catch (err) { setError(err.response?.data?.error || 'Error al iniciar sesión'); }
    finally { setLoading(false); }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="var(--primary)"/><path d="M8 22L16 10L24 22H8Z" fill="white" fillOpacity="0.9"/><circle cx="16" cy="10" r="3" fill="var(--accent)"/></svg>
          <span>TalentosRD</span>
        </div>
        <h1>Bienvenido de vuelta</h1>
        <p className={styles.subtitle}>Accede a tu cuenta para gestionar tu empleo</p>
        {error && <div className="alert alert-error">{error}</div>}
        {linkedinEnabled && <>
          <a href={`/api/auth/linkedin/start?country_code=${getCountryCode()}`} className={styles.linkedinButton}><span>in</span> Continuar con LinkedIn</a>
          <div className={styles.orDivider}><span>o usa tu correo</span></div>
        </>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" required value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="tu@email.com" autoComplete="email"/>
          </div>
          <div className="form-group">
            <div className={styles.labelRow}><label>Contraseña</label><Link to="/forgot-password" className={styles.forgot}>¿Olvidaste tu contraseña?</Link></div>
            <div className={styles.passField}>
              <input type={showPass?'text':'password'} required value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} placeholder="••••••••" autoComplete="current-password"/>
              <button type="button" onClick={()=>setShowPass(p=>!p)} className={styles.eyeBtn}>{showPass?'👁️':'👁️‍🗨️'}</button>
            </div>
          </div>
          <button type="submit" disabled={loading} className={`btn btn-primary ${styles.submitBtn}`}>
            {loading ? <><span className="spinner" style={{width:16,height:16}}/>Cargando...</> : 'Iniciar sesión'}
          </button>
        </form>
        <p className={styles.switch}>¿No tienes cuenta? <Link to="/register">Regístrate gratis</Link></p>
        <div className={styles.demoNote}>
          <strong>Demo:</strong> empresa@talentosrd.com / Admin123!
        </div>
      </div>
    </div>
  );
}

export function LinkedInCallback() {
  const { completeLinkedIn } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    const code = params.get('code');
    const providerError = params.get('error');
    if (providerError || !code) { setError('No se pudo completar el acceso con LinkedIn. Intenta nuevamente.'); return; }
    completeLinkedIn(code).then(data => {
      navigate(data.role === 'company' ? '/dashboard/company' : '/dashboard/candidate', { replace:true });
    }).catch(err => setError(err.response?.data?.error || 'El acceso con LinkedIn no pudo completarse.'));
  }, [completeLinkedIn, navigate, params]);

  return <div className={styles.page}><div className={styles.card}>
    <h1>Acceso con LinkedIn</h1>
    {error ? <><div className="alert alert-error">{error}</div><Link className={`btn btn-primary ${styles.submitBtn}`} to="/login">Volver al inicio</Link></> :
      <div className={styles.oauthLoading}><div className="spinner"/><span>Completando acceso seguro…</span></div>}
  </div></div>;
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault(); setLoading(true); setMessage('');
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setMessage(data.message);
    } catch (error) {
      setMessage(error.response?.data?.error || 'No se pudo procesar la solicitud.');
    } finally { setLoading(false); }
  };

  return (
    <div className={styles.page}><div className={styles.card}>
      <h1>Recuperar contraseña</h1>
      <p className={styles.subtitle}>Te enviaremos un enlace seguro si el correo pertenece a una cuenta.</p>
      {message && <div className="alert alert-success">{message}</div>}
      <form onSubmit={submit}>
        <div className="form-group"><label>Correo electrónico</label>
          <input type="email" required autoComplete="email" value={email} onChange={e=>setEmail(e.target.value)} />
        </div>
        <button className={`btn btn-primary ${styles.submitBtn}`} disabled={loading}>{loading?'Enviando...':'Enviar enlace'}</button>
      </form>
      <p className={styles.switch}><Link to="/login">← Volver al inicio de sesión</Link></p>
    </div></div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password:'', confirm:'' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const token = params.get('token');

  const submit = async (event) => {
    event.preventDefault(); setError('');
    if (!token) return setError('El enlace no contiene un token válido.');
    if (form.password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres.');
    if (form.password !== form.confirm) return setError('Las contraseñas no coinciden.');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: form.password });
      navigate('/login', { replace:true });
    } catch (err) { setError(err.response?.data?.error || 'No se pudo restablecer la contraseña.'); }
    finally { setLoading(false); }
  };

  return (
    <div className={styles.page}><div className={styles.card}>
      <h1>Nueva contraseña</h1>
      <p className={styles.subtitle}>Elige una contraseña de al menos 8 caracteres.</p>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={submit}>
        <div className="form-group"><label>Nueva contraseña</label>
          <input type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))}/>
        </div>
        <div className="form-group"><label>Confirmar contraseña</label>
          <input type="password" required minLength={8} autoComplete="new-password" value={form.confirm} onChange={e=>setForm(p=>({...p,confirm:e.target.value}))}/>
        </div>
        <button className={`btn btn-primary ${styles.submitBtn}`} disabled={loading}>{loading?'Actualizando...':'Guardar contraseña'}</button>
      </form>
    </div></div>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get('role') || 'candidate');
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email:'', password:'', confirmPassword:'', full_name:'', company_name:'', industry:'', city:'', province:'' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const validateStep1 = () => {
    const e = {};
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Email inválido';
    if (form.password.length < 8) e.password = 'Mínimo 8 caracteres';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Las contraseñas no coinciden';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (role==='candidate' && !form.full_name) e.full_name = 'Nombre requerido';
    if (role==='company' && !form.company_name) e.company_name = 'Nombre de empresa requerido';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) { if (validateStep1()) setStep(2); return; }
    if (!validateStep2()) return;
    setLoading(true);
    try {
      await register({ ...form, role, country_code:getCountryCode() });
      navigate(role==='company' ? '/dashboard/company' : '/dashboard/candidate');
    } catch (err) { setErrors({ submit: err.response?.data?.error || 'Error al registrar' }); }
    finally { setLoading(false); }
  };

  const INDUSTRIES = ['Tecnología','Marketing','Finanzas','Salud','Educación','Turismo','Construcción','Retail','Manufactura','Servicios','Otro'];

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><rect width="32" height="32" rx="8" fill="var(--primary)"/><path d="M8 22L16 10L24 22H8Z" fill="white" fillOpacity="0.9"/><circle cx="16" cy="10" r="3" fill="var(--accent)"/></svg>
          <span>TalentosRD</span>
        </div>
        <h1>Crear cuenta gratis</h1>

        <div className={styles.roleToggle}>
          <button className={role==='candidate'?styles.active:''} onClick={()=>setRole('candidate')}>👤 Soy candidato</button>
          <button className={role==='company'?styles.active:''} onClick={()=>setRole('company')}>🏢 Soy empresa</button>
        </div>

        <div className={styles.steps}>
          <div className={`${styles.stepDot} ${step>=1?styles.stepActive:''}`}>1</div>
          <div className={styles.stepLine}/>
          <div className={`${styles.stepDot} ${step>=2?styles.stepActive:''}`}>2</div>
        </div>

        {errors.submit && <div className="alert alert-error">{errors.submit}</div>}

        <form onSubmit={handleSubmit}>
          {step===1 && <>
            <div className="form-group">
              <label>Correo electrónico</label>
              <input type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="tu@email.com"/>
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>
            <div className="form-group">
              <label>Contraseña</label>
              <input type="password" value={form.password} onChange={e=>set('password',e.target.value)} placeholder="Mínimo 8 caracteres"/>
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>
            <div className="form-group">
              <label>Confirmar contraseña</label>
              <input type="password" value={form.confirmPassword} onChange={e=>set('confirmPassword',e.target.value)} placeholder="Repite tu contraseña"/>
              {errors.confirmPassword && <span className="form-error">{errors.confirmPassword}</span>}
            </div>
          </>}

          {step===2 && <>
            {role==='candidate' ? <>
              <div className="form-group">
                <label>Nombre completo</label>
                <input value={form.full_name} onChange={e=>set('full_name',e.target.value)} placeholder="Juan Pérez"/>
                {errors.full_name && <span className="form-error">{errors.full_name}</span>}
              </div>
            </> : <>
              <div className="form-group">
                <label>Nombre de la empresa</label>
                <input value={form.company_name} onChange={e=>set('company_name',e.target.value)} placeholder="Empresa S.A."/>
                {errors.company_name && <span className="form-error">{errors.company_name}</span>}
              </div>
              <div className="form-group">
                <label>Sector / Industria</label>
                <select value={form.industry} onChange={e=>set('industry',e.target.value)}>
                  <option value="">Seleccionar sector</option>
                  {INDUSTRIES.map(i=><option key={i}>{i}</option>)}
                </select>
              </div>
            </>}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div className="form-group">
                <label>{getCountry().divisionSingular}</label>
                <select value={form.province} onChange={e=>set('province',e.target.value)}>
                  <option value="">Seleccionar {getCountry().divisionSingular}</option>
                  {COUNTRY_DIVISIONS.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Municipio</label><MunicipalitySelect province={form.province} value={form.city} onChange={e=>set('city',e.target.value)}/></div>
            </div>
          </>}

          <div style={{display:'flex',gap:'8px'}}>
            {step===2 && <button type="button" onClick={()=>setStep(1)} className="btn btn-ghost" style={{flex:1}}>← Atrás</button>}
            <button type="submit" disabled={loading} className={`btn btn-primary ${styles.submitBtn}`} style={{flex:2}}>
              {loading ? 'Creando cuenta...' : step===1 ? 'Continuar →' : 'Crear cuenta'}
            </button>
          </div>
        </form>

        <p className={styles.terms}>Al registrarte aceptas nuestros <Link to="/terms">Términos de uso</Link> y <Link to="/privacy">Política de privacidad</Link>.</p>
        <p className={styles.switch}>¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link></p>
      </div>
    </div>
  );
}
